import React, { useState } from "react";
import { PencilIcon, UserIcon } from "@heroicons/react/solid";
import { Link } from "react-router-dom";
import useGetAccountDetails from "../../hooks/MyAccount/useGetAccountDetails";
import { toast } from "react-hot-toast";
import Spinner from "../../components/loader/Spinner";
import { Button } from "../../components/ui";

const MyAccount = () => {
  const [userData, setUserData] = useState(null);

  // Fetch user data
  const { list } = useGetAccountDetails({
    onSuccess: (response) => {
      if (response?.data) {
        setUserData(response.data);
      }
    },
    onError: (error) => {
      toast.error("Failed to load profile data");
    },
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") return "--";

    try {
      const date = new Date(dateStr);

      // Check if date is valid
      if (isNaN(date.getTime())) return "--";

      // Format date to dd/mm/yyyy
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "--";
    }
  };

  if (list.isLoading || list.isFetching) {
    return <Spinner fullWidth={true} />;
  }

  return (
    <div className="p-2 sm:p-4 mb-10">
      <div className="flex justify-between items-center my-3 sm:my-5">
        <h1 className="text-xl sm:text-2xl font-semibold">My Account</h1>
        <Link to="/my-account/edit">
          <Button className="space-x-2 bg-white">
            <PencilIcon className="h-5 w-5 text-blue-600" />
            <span className="text-blue-600 font-medium">Edit Profile</span>
          </Button>
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row justify-between border p-3 sm:p-5 rounded-lg shadow-md">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-5 items-center">
          {userData?.profile_image_url ? (
            <img
              src={userData.profile_image_url}
              alt="Profile"
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover"
            />
          ) : (
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gray-200 flex items-center justify-center">
              <UserIcon className="h-12 w-12 text-gray-400" />
            </div>
          )}
          <div className="flex flex-col justify-center text-center sm:text-left">
            <span className="text-lg font-semibold">
              {userData?.name || ""}
            </span>
            <span className="text-sm sm:text-base">
              {userData?.username || ""}
            </span>
            <span className="text-sm sm:text-base">
              {userData?.email || ""}
            </span>
            <span className="text-sm sm:text-base">
              {userData?.designation || ""}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6 sm:mt-10 border p-3 sm:p-5 rounded-lg shadow-md">
        <h1 className="text-lg sm:text-xl font-semibold mb-3">
          Personal Information
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 sm:mt-5">
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Username
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.username || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Full Name
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.name || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Email Address
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.email || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Phone
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.phone ? `+91 ${userData.phone}` : "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Date of Birth
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {formatDate(userData?.date_of_birth)}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Gender
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500 capitalize">
              {userData?.gender || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Designation
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.designation || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Department
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.department || "--"}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6 sm:mt-10 border p-3 sm:p-5 rounded-lg shadow-md">
        <h1 className="text-lg sm:text-xl font-semibold mb-3">Address</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 sm:mt-5">
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Line 1
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.p_address_1 || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Line 2
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.p_address_2 || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Pincode
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.p_pincode || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              City
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.p_city || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              State
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.p_state || "--"}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6 sm:mt-10 border p-3 sm:p-5 rounded-lg shadow-md">
        <h1 className="text-lg sm:text-xl font-semibold mb-3">
          Identity Information
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 sm:mt-5">
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Aadhar Number
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.aadhar || "--"}
            </span>
          </div>
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              PAN Number
            </label>
            <span className="block mt-1 text-sm sm:text-base text-gray-500">
              {userData?.pan || "--"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
